import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/shpage/indent`;

const SHIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const { role } = useSelector((state) => state.auth);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });



  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const navigate = useNavigate();
  const getApiEndpoint = () => {
    return role === "admin" 
      ? `${API_BASE_URL}/adminpage/shindent` 
      : `${API_BASE_URL}/shpage/indent`;
  };
  const fetchIndents = async () => {
    try {
      const apiEndpoint = getApiEndpoint();
      const response = await axios.get(apiEndpoint);
      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          IR: indent.IR ? "Received" : "Pending",
          SH: indent.SH ? "Received" : "Pending",
          CreationDate: new Date(indent.CreationDate)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"), // Format as dd-mm-yyyy
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Error fetching indents:", error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    if (!query) {
      setFilteredIndents(indents);
    } else {
      const lowercaseQuery = query.toLowerCase();
      const filtered = indents.filter((indent) => {
        // Convert all potentially searchable fields to lowercase for case-insensitive search
        return (
          indent.IndentID.toString().toLowerCase().includes(lowercaseQuery) ||
          (indent.Status &&
            indent.Status.toLowerCase().includes(lowercaseQuery)) ||
          (indent.ProjectName &&
            indent.ProjectName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.TypeName &&
            indent.TypeName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.CreationDate &&
            indent.CreationDate.toString()
              .toLowerCase()
              .includes(lowercaseQuery))
        );
      });
      setFilteredIndents(filtered);
    }
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)
        )
      );
    } else {
      setFilteredIndents(indents);
    }
  };

  // Button click handlers for redirection
  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/SHtable`, { state: { row } });
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Store Head Page</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            editRoute="/SHtable" // Added editRoute prop with default value
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole={role} // Add this line
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SHIndent;
